/**
 * The error steps and listed error types (no imports): shared by the diagnostic report (./report.ts re-exports them)
 * and the beta recent-errors log (src/beta/recentErrors.ts), which the app imports statically; kept apart so that import
 * doesn't pull the whole report module (masking, skeletons, parsers) into the start bundle (docs/architecture.md "Size budget").
 */
/**
 * Where an error happened. Besides the four steps, the reader's startup stages (src/upload/pdfReader.ts,
 * src/ocr/browser.ts): reading the file from the phone, loading PDF.js, starting a worker (spawn: the
 * constructor threw; module: its script failed to load or run; timeout: never ready), compiling
 * WebAssembly, starting OCR, and CSP violations (by directive).
 */
export const ERROR_STEPS = ['upload', 'parse', 'rules', 'ui', 'fileRead', 'fileReadArrayBuffer', 'fileReadFileReader', 'pdfjsLoad', 'pdfOpen', 'pdfText', 'workerSpawn', 'workerModule', 'workerTimeout', 'wasmCompile', 'ocrStart', 'readerTimeout', 'csp'] as const;
export type ErrorStep = (typeof ERROR_STEPS)[number];
/** Error types a report may name; anything else is 'other'. Never the message, stack or values. */
export const ERROR_CODES = [
  'Error', 'TypeError', 'RangeError', 'SyntaxError', 'ReferenceError', 'URIError', 'EvalError', 'AggregateError',
  'InvalidPDFException', 'MissingPDFException', 'PasswordException', 'UnexpectedResponseException', 'UnknownErrorException', 'FormatError', 'AbortException',
  'AbortError', 'NotAllowedError', 'NotFoundError', 'NotReadableError', 'QuotaExceededError', 'SecurityError', 'DataCloneError',
  // WebAssembly, a worker that never said ready, the OCR engine's own error.
  'CompileError', 'LinkError', 'RuntimeError', 'Timeout', 'OcrEngineUnavailable',
  // CSP violations: the directive (never the blocked address).
  'worker-src', 'script-src', 'script-src-elem', 'script-src-attr', 'connect-src', 'img-src', 'style-src', 'style-src-elem', 'style-src-attr', 'default-src', 'child-src', 'font-src',
  'other',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

/** The error's type, if it is one we list; its message and stack are never read. */
export function errorCode(err: unknown): ErrorCode {
  // A CSP violation is recorded as its directive (a plain string from the event; nothing else of it is kept).
  if (typeof err === 'string') return (ERROR_CODES as readonly string[]).includes(err) ? (err as ErrorCode) : 'other';
  const name = err instanceof Error || (typeof DOMException !== 'undefined' && err instanceof DOMException) ? err.name : undefined;
  return (ERROR_CODES as readonly string[]).includes(name ?? '') ? (name as ErrorCode) : 'other';
}
